import React, { useMemo, useState } from "react";
import { ArrowsClockwise, Copy, Paragraph } from "@phosphor-icons/react";
import {
  Button,
  Group,
  Stack,
  Switch,
  Text,
  Textarea,
} from "@mantine/core";

function removeLineBreaks(value: string, keepParagraphs: boolean): string {
  // Normalize line endings.
  const normalized = value.replace(/\r\n?/g, "\n");

  if (keepParagraphs) {
    // Collapse single line breaks within a paragraph into a space, but keep
    // paragraph breaks (two or more consecutive newlines).
    return normalized
      .split(/\n{2,}/)
      .map((paragraph) =>
        paragraph
          .split("\n")
          .map((line) => line.trim())
          .filter(Boolean)
          .join(" "),
      )
      .filter(Boolean)
      .join("\n\n");
  }

  // Remove every line break, collapsing everything into a single line.
  return normalized
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .join(" ");
}

export default function RemoveLineBreaksPage() {
  const [inputValue, setInputValue] = useState("");
  const [keepParagraphs, setKeepParagraphs] = useState(true);

  const outputValue = useMemo(() => {
    if (!inputValue.trim()) return "";
    return removeLineBreaks(inputValue, keepParagraphs);
  }, [inputValue, keepParagraphs]);

  const copyOutput = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(outputValue);
    } catch (error) {
      console.error("Kopyalama başarısız:", error);
    }
  };

  return (
    <div className="tool-page">
      <div className="tool-glass-panel">
        <Stack gap="md">
          <Group justify="space-between">
            <div className="tool-page-title">
              <Paragraph size={28} weight="duotone" />
              <Text fw={600} size="lg">Satır Sonlarını Kaldır</Text>
            </div>
            <Switch
              checked={keepParagraphs}
              onChange={(event) => setKeepParagraphs(event.currentTarget.checked)}
              label="Paragrafları Koru"
            />
          </Group>

          <Textarea
            value={inputValue}
            onChange={(event) => setInputValue(event.currentTarget.value)}
            onKeyDown={(event) => {
              if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
                event.preventDefault();
                if (outputValue) copyOutput();
              }
            }}
            minRows={7}
            autosize
            label="Girdi Metni"
            placeholder="Metni buraya yapıştır... (⌘/Ctrl + Enter ile kopyala)"
            autoFocus
          />

          <Textarea
            value={outputValue}
            readOnly
            minRows={7}
            autosize
            label="Sonuç"
            placeholder="Sonuç burada görünecek..."
          />

          <Group justify="flex-end">
            <Button
              variant="default"
              leftSection={<ArrowsClockwise size={16} />}
              onClick={() => setInputValue("")}
            >
              Temizle
            </Button>
            <Button
              onClick={copyOutput}
              disabled={!outputValue}
              leftSection={<Copy size={16} />}
            >
              Kopyala
            </Button>
          </Group>
        </Stack>
      </div>
    </div>
  );
}
